import { useMutation } from '@tanstack/react-query';
import { T, Var, useGT } from 'gt-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { orpc } from '@/api/rpc/client';
import { useToast } from 'src/context/ToastContext';
import { CompanyPicker } from '@/components/company/CompanyPicker';
import { useCompanyPicker } from '@/components/company/useCompanyPicker';
import { pricelistVendorName } from '@/components/company/companyPickerDisplay';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The pricelist PDF document being (re)assigned. */
  pdfDocumentId: string | null;
  /** Name of the company the pricelist is currently assigned to, if any. */
  currentCompanyName?: string | null;
  onAssigned: () => void;
}

export function PricelistAssignDialog({
  open,
  onOpenChange,
  pdfDocumentId,
  currentCompanyName,
  onAssigned,
}: Props) {
  const gt = useGT();
  const { showToast } = useToast();
  const picker = useCompanyPicker(null, { forPricelistManagement: true });

  const isReassign =
    currentCompanyName !== undefined && currentCompanyName !== null;

  // Reset picker state on close in the event handler rather than an effect, so
  // we don't trigger cascading renders by setting state during render/commit.
  function handleOpenChange(next: boolean) {
    if (!next) {
      picker.reset();
    }
    onOpenChange(next);
  }

  const assignMutation = useMutation(
    orpc.inventoryManagement.pricelist.assignToCompany.mutationOptions(),
  );

  async function handleSubmit() {
    if (!pdfDocumentId || !picker.selectedCompany) return;
    try {
      await assignMutation.mutateAsync({
        pdfDocumentId,
        companyId: picker.selectedCompany.id,
      });
      showToast({
        title: gt('Assigned', {
          context: 'toast title when a pricelist is assigned to a company',
        }),
        message: gt('Pricelist assigned to {company}.', {
          context: 'toast message when a pricelist is assigned to a company',
          company: pricelistVendorName(picker.selectedCompany.name),
        }),
        color: 'success',
      });
      onAssigned();
      handleOpenChange(false);
    } catch {
      showToast({
        title: gt('Error', { context: 'toast title for error state' }),
        message: gt('Failed to assign pricelist.', {
          context: 'toast message when assigning a pricelist fails',
        }),
        color: 'error',
      });
    }
  }

  const isPending = assignMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isReassign ? (
              <T context="title of the pricelist reassign dialog">
                Reassign Pricelist
              </T>
            ) : (
              <T context="title of the pricelist assign dialog">
                Assign Pricelist
              </T>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2 overflow-hidden">
          {isReassign && (
            <p className="text-sm text-muted-foreground">
              <T context="current-assignment note in pricelist reassign dialog">
                Currently assigned to{' '}
                <Var>
                  <span className="font-medium text-foreground">
                    {pricelistVendorName(currentCompanyName ?? '')}
                  </span>
                </Var>
                .
              </T>
            </p>
          )}

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">
              <T context="label for vendor-number selector in pricelist assign dialog">
                Vendor Number
              </T>
            </span>
            <CompanyPicker
              picker={picker}
              disabled={isPending}
              triggerPlaceholder={gt('Select a vendor number…', {
                context:
                  'placeholder for vendor-number selector in pricelist assign dialog',
              })}
              searchPlaceholder={gt(
                'Search by vendor number, company name, or company code…',
                {
                  context:
                    'pricelist assign dialog search placeholder covering 5-digit vendor number, name, and company code',
                },
              )}
              emptyState={
                <T context="empty state when vendor-number search yields no results in assign dialog">
                  No vendor numbers found
                </T>
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            <T context="cancel button in pricelist assign dialog">Cancel</T>
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!picker.selectedCompany || isPending}
          >
            {isPending ? (
              <T context="submit button label while pricelist assignment is in progress">
                Assigning…
              </T>
            ) : (
              <T context="submit button label in pricelist assign dialog">
                Assign
              </T>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
